/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";

import {
	TextField,
	InputAdornment,
	Button,
	Checkbox,
	Select,
	MenuItem,
	Pagination,
	Autocomplete,
	FormControl,
	InputLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import dayjs from "dayjs";
import { THEME_MAP } from "../theme";
import { useBars, useBarUtils } from "../actions/bars";
import { useUserUtils } from "../actions/users";
import { useRegions, useFetchRegions } from "../actions/regions";

const normalizeBarSnapshot = (input) => {
	if (!input) {
		return null;
	}

	if (typeof input?.data === "function") {
		const data = input.data();
		return {
			...data,
			barId: input.id,
			id: input.id
		};
	}

	const barId = input.barId || input.id;
	return {
		...input,
		barId,
		id: barId
	};
};

const collator = new Intl.Collator("en", { sensitivity: "base" });
const EARTH_RADIUS_MILES = 3958.8;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const toRadians = (value) => (value * Math.PI) / 180;

const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
	if (
		typeof lat1 !== "number" ||
		typeof lon1 !== "number" ||
		typeof lat2 !== "number" ||
		typeof lon2 !== "number"
	) {
		return null;
	}

	const deltaLat = toRadians(lat2 - lat1);
	const deltaLon = toRadians(lon2 - lon1);
	const a =
		Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
		Math.cos(toRadians(lat1)) *
			Math.cos(toRadians(lat2)) *
			Math.sin(deltaLon / 2) *
			Math.sin(deltaLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return EARTH_RADIUS_MILES * c;
};

const formatCityState = (bar) => {
	const parts = parseAddressParts(bar?.barAddress);
	const city = normalizeWhitespace(bar?.city || parts.city || "");
	const state = normalizeWhitespace(bar?.state || parts.state || "");
	if (!city && !state) {
		return "";
	}
	return state ? `${city}, ${state}` : city;
};

const formatDateTime = (value) => {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : "—";
};

const normalizeWhitespace = (value = "") => value.replace(/\s+/g, " ").trim();
const isStateLikeSegment = (value) => /^[A-Za-z]{2}(?:\s*\d{5}(?:-\d{4})?)?$/.test(value);
const segmentHasDigit = (value) => /\d/.test(value);
const looksLikeCityName = (value) =>
	Boolean(value) && /[A-Za-z]/.test(value) && !segmentHasDigit(value) && !isStateLikeSegment(value);

const parseAddressParts = (address) => {
	const defaults = {
		street: "",
		city: "",
		state: "",
		postalCode: "",
		country: ""
	};

	if (!address) {
		return defaults;
	}

	if (Array.isArray(address)) {
		const [street = "", city = "", state = "", postalCode = "", country = ""] = address;
		return {
			street: normalizeWhitespace(street),
			city: normalizeWhitespace(city),
			state: normalizeWhitespace(state),
			postalCode: normalizeWhitespace(postalCode),
			country: normalizeWhitespace(country)
		};
	}

	if (typeof address !== "string") {
		return defaults;
	}

	const normalizedAddress = normalizeWhitespace(address.replace(/\n+/g, ","));
	const segments = normalizedAddress
		.split(",")
		.map((segment) => normalizeWhitespace(segment))
		.filter(Boolean);

	if (!segments.length) {
		return defaults;
	}

	const street = segments[0] || "";
	let country = "";
	let stateSegment = "";
	let city = "";

	if (segments.length >= 4) {
		country = segments[segments.length - 1];
		stateSegment = segments[segments.length - 2];
		city = segments[segments.length - 3];
	} else if (segments.length === 3) {
		stateSegment = segments[2];
		city = segments[1];
	} else if (segments.length === 2) {
		city = segments[1];
	}

	let state = "";
	let postalCode = "";
	if (stateSegment) {
		const stateMatch = stateSegment.match(/^([A-Za-z]{2})(?:\s+(.+))?$/);
		if (stateMatch) {
			state = stateMatch[1] || "";
			postalCode = stateMatch[2] ? stateMatch[2].trim() : "";
		} else {
			state = stateSegment;
		}
	}

	return {
		street,
		city,
		state,
		postalCode,
		country
	};
};

const extractCityFromAddress = (address) => {
	if (!address) {
		return "";
	}

	if (Array.isArray(address)) {
		// Expected structure: [street, city, state, zip, country]
		const candidate = normalizeWhitespace(address[1] || "");
		if (looksLikeCityName(candidate)) {
			return candidate;
		}
		// Fallback: scan remaining segments for something that looks like a city
		for (let index = 0; index < address.length; index += 1) {
			const value = normalizeWhitespace(address[index] || "");
			if (looksLikeCityName(value)) {
				return value;
			}
		}
		return candidate;
	}

	if (typeof address !== "string") {
		return "";
	}

	const normalizedAddress = address.replace(/\n+/g, ",");

	const segments = normalizedAddress
		.split(",")
		.map((segment) => normalizeWhitespace(segment))
		.filter(Boolean);

	if (!segments.length) {
		return "";
	}

	for (let index = segments.length - 2; index >= 0; index -= 1) {
		const segment = segments[index];
		if (looksLikeCityName(segment)) {
			return segment;
		}
	}

	const fallback =
		segments.find((segment) => looksLikeCityName(segment)) ||
		segments.find((segment) => segment && !isStateLikeSegment(segment)) ||
		segments[0];
	return fallback || "";
};

const getBarCityLabel = (bar) => {
	if (!bar) {
		return "";
	}

	const candidates = [
		normalizeWhitespace(bar.city || ""),
		normalizeWhitespace(parseAddressParts(bar.barAddress).city),
		normalizeWhitespace(extractCityFromAddress(bar.barAddress || ""))
	].filter(Boolean);

	const match = candidates.find((value) => looksLikeCityName(value));
	return match || "";
};

export function Bars() {
	const history = useHistory();
	const { search } = useLocation();
	const { bars, endOfResults, loading } = useBars();
	const { getNext, clearSelectedBar, setBarsQuery, assignRegionsToBars } = useBarUtils();
	const regions = useRegions();
	const { fetchRegions } = useFetchRegions();

	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCity, setSelectedCity] = useState(null);
	const [selectedRegion, setSelectedRegion] = useState(null);
	const [sortMode, setSortMode] = useState("alphabetical");
	const [radiusMiles, setRadiusMiles] = useState("");
	const [radiusInput, setRadiusInput] = useState("");
	const [assignMode, setAssignMode] = useState(false);
	const [selectedBarIds, setSelectedBarIds] = useState(() => new Set());
	const [assignRegionId, setAssignRegionId] = useState("");
	const [confirmAssignOpen, setConfirmAssignOpen] = useState(false);
	const [assigning, setAssigning] = useState(false);
	const [assignError, setAssignError] = useState(null);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [currentPage, setCurrentPage] = useState(1);

	const assignRegionName = useMemo(() => {
		return regions.find((region) => region.id === assignRegionId)?.Name || "";
	}, [assignRegionId, regions]);

	const barsLength = bars?.length || 0;

	useEffect(() => {
		fetchRegions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setRadiusInput(radiusMiles);
	}, [radiusMiles]);

	const selectedCityCenter = useMemo(() => {
		if (!selectedCity?.key) {
			return null;
		}

		const matches = (bars || []).filter((bar) => {
			const label = getBarCityLabel(bar);
			return label && label.toLowerCase() === selectedCity.key;
		});
		const withCoordinates = matches.filter(
			(bar) => Number.isFinite(bar.latitude) && Number.isFinite(bar.longitude)
		);
		if (!withCoordinates.length) {
			return null;
		}

		const { latitude, longitude } = withCoordinates.reduce(
			(acc, bar) => ({
				latitude: acc.latitude + bar.latitude,
				longitude: acc.longitude + bar.longitude
			}),
			{ latitude: 0, longitude: 0 }
		);

		return {
			latitude: latitude / withCoordinates.length,
			longitude: longitude / withCoordinates.length
		};
	}, [bars, selectedCity]);

	const radiusValue = Number.parseFloat(radiusMiles);
	const radiusInputNumber = Number.parseFloat(radiusInput);
	const hasCitySelection = Boolean(selectedCity);
	const canComputeDistances = Boolean(selectedCityCenter);
	const radiusInputValid =
		radiusInput === "" || (Number.isFinite(radiusInputNumber) && radiusInputNumber >= 0);
	const canApplyRadius = hasCitySelection && radiusInputValid;

	useEffect(() => {
		const params = new URLSearchParams(search);
		const q = params.get("q") || "";
		if (q !== searchTerm) {
			setSearchTerm(q);
			setBarsQuery(q);
		}
	}, [search, searchTerm, setBarsQuery]);

	useEffect(() => {
		if (!assignMode) {
			setSelectedBarIds(new Set());
			setAssignRegionId("");
		}
	}, [assignMode]);

	const handleSearchChange = useCallback(
		(event) => {
			const value = event.target.value;
			setSearchTerm(value);
			setCurrentPage(1);

			const nextSearch = value ? `?q=${encodeURIComponent(value)}` : "";
			if (nextSearch !== search) {
				history.replace({ search: nextSearch });
			}

			setBarsQuery(value);
		},
		[history, search, setBarsQuery]
	);

	const cityOptions = useMemo(() => {
		const map = new Map();
		(bars || []).forEach((bar) => {
			const cityLabel = getBarCityLabel(bar);
			if (!cityLabel) {
				return;
			}
			const key = cityLabel.toLowerCase();
			if (!map.has(key)) {
				map.set(key, {
					key,
					label: cityLabel
				});
			}
		});
		return Array.from(map.values()).sort((a, b) => collator.compare(a.label, b.label));
	}, [bars]);

	useEffect(() => {
		if (!selectedCity || searchTerm || endOfResults) {
			return;
		}

		getNext();
	}, [selectedCity, searchTerm, endOfResults, barsLength, getNext]);

	const processedBars = useMemo(() => {
		const unique = new Map();
		(bars || []).forEach((bar) => {
			const key = bar?.barId || bar?.id;
			if (!key || unique.has(key)) {
				return;
			}
			unique.set(key, bar);
		});
		const list = Array.from(unique.values());

		const withDistance = list.map((bar) => {
			const distance = selectedCityCenter &&
				Number.isFinite(bar.latitude) &&
				Number.isFinite(bar.longitude)
					? calculateDistanceMiles(
						selectedCityCenter.latitude,
						selectedCityCenter.longitude,
						bar.latitude,
						bar.longitude
					)
					: null;
			return { bar, distance };
		});

		const radiusFilterActive =
			Boolean(selectedCityCenter) && Number.isFinite(radiusValue) && radiusValue > 0;

		let filtered = withDistance;
		if (radiusFilterActive) {
			filtered = filtered.filter((item) => {
				if (item.distance !== null && item.distance <= radiusValue) {
					return true;
				}
				if (selectedCity?.key) {
					const label = getBarCityLabel(item.bar);
					return label && label.toLowerCase() === selectedCity.key;
				}
				return false;
			});
		} else if (selectedCity?.key) {
			filtered = filtered.filter((item) => {
				const label = getBarCityLabel(item.bar);
				return label && label.toLowerCase() === selectedCity.key;
			});
		}

		if (!assignMode && selectedRegion) {
			filtered = filtered.filter((item) => item.bar.regionId === selectedRegion);
		}

		if (assignMode) {
			filtered = filtered.filter((item) => !item.bar.regionId);
		}

		const sorted = [...filtered];
		if (sortMode === "lastUpdated") {
			sorted.sort((left, right) => {
				const leftValue = dayjs(left.bar.updatedAt || left.bar.lastPromotionUpdateUTC || left.bar.createdAt);
				const rightValue = dayjs(right.bar.updatedAt || right.bar.lastPromotionUpdateUTC || right.bar.createdAt);
				return rightValue.valueOf() - leftValue.valueOf();
			});
		} else if (sortMode === "distance" && selectedCityCenter) {
			sorted.sort((left, right) => {
				if (left.distance === null && right.distance === null) {
					return collator.compare(left.bar.barName || "", right.bar.barName || "");
				}
				if (left.distance === null) {
					return 1;
				}
				if (right.distance === null) {
					return -1;
				}
				return left.distance - right.distance;
			});
		} else {
			sorted.sort((left, right) =>
				collator.compare(left.bar.barName || "", right.bar.barName || "")
			);
		}

		return sorted;
	}, [assignMode, bars, radiusValue, selectedCity, selectedCityCenter, selectedRegion, sortMode]);

	const canPaginate = !searchTerm;

	const totalPages = useMemo(() => {
		const pages = Math.ceil(processedBars.length / pageSize);
		return Math.max(1, pages || 1);
	}, [processedBars.length, pageSize]);

	const paginationCount = useMemo(() => Math.max(1, totalPages), [totalPages]);

	const [pendingPage, setPendingPage] = useState(null);
	const [pendingLast, setPendingLast] = useState(false);

	const paginationPage = useMemo(() => {
		return Math.min(Math.max(1, currentPage), paginationCount);
	}, [currentPage, paginationCount]);

	useEffect(() => {
		const maxPage = paginationCount;
		if (currentPage > maxPage) {
			setCurrentPage(maxPage);
		}
	}, [currentPage, paginationCount]);

	const paginatedBars = useMemo(() => {
		const start = (paginationPage - 1) * pageSize;
		return processedBars.slice(start, start + pageSize);
	}, [paginationPage, processedBars, pageSize]);

	const visibleBarIds = useMemo(
		() => paginatedBars.map(({ bar }) => bar?.barId).filter(Boolean),
		[paginatedBars]
	);

	const pageStartIndex = useMemo(() => {
		if (!paginatedBars.length) {
			return 0;
		}
		return (paginationPage - 1) * pageSize + 1;
	}, [paginationPage, pageSize, paginatedBars.length]);

	const pageEndIndex = useMemo(() => {
		if (!paginatedBars.length) {
			return 0;
		}
		return (paginationPage - 1) * pageSize + paginatedBars.length;
	}, [paginationPage, pageSize, paginatedBars.length]);

	const totalKnownLabel = useMemo(() => {
		const suffix = !endOfResults && canPaginate ? "+" : "";
		return `${processedBars.length}${suffix}`;
	}, [canPaginate, endOfResults, processedBars.length]);

	const barLabel = processedBars.length === 1 ? "bar" : "bars";

	const pageSummaryText = useMemo(() => {
		if (loading && processedBars.length === 0) {
			return "Loading bars...";
		}

		if (processedBars.length === 0) {
			return "No bars to display";
		}

		if ((pendingLast || pendingPage !== null) && !endOfResults) {
			return "Fetching more bars...";
		}

		if (loading && paginatedBars.length === 0 && !endOfResults && canPaginate) {
			const start = (paginationPage - 1) * pageSize + 1;
			const end = start + pageSize - 1;
			return `Loading ${start}-${end}...`;
		}

		return `${pageStartIndex}-${pageEndIndex} of ${totalKnownLabel} ${barLabel}`;
	}, [barLabel, canPaginate, endOfResults, pageEndIndex, pageSize, pageStartIndex, paginatedBars.length, pendingLast, pendingPage, processedBars.length, totalKnownLabel, loading, paginationPage]);

	useEffect(() => {
		if (!canPaginate || loading) {
			return;
		}

		if (pendingLast) {
			if (endOfResults) {
				setCurrentPage(Math.max(1, totalPages));
				setPendingLast(false);
			} else {
				getNext();
			}
		}
	}, [canPaginate, endOfResults, getNext, loading, pendingLast, totalPages]);

	useEffect(() => {
		if (!canPaginate || loading || pendingLast || pendingPage === null) {
			return;
		}

		const requiredCount = (pendingPage - 1) * pageSize + 1;
		if (processedBars.length >= requiredCount || endOfResults) {
			setCurrentPage(Math.min(pendingPage, totalPages));
			setPendingPage(null);
			return;
		}

		getNext();
	}, [canPaginate, endOfResults, getNext, loading, pageSize, pendingLast, pendingPage, processedBars.length, totalPages]);

	useEffect(() => {
		if (!assignMode) {
			return;
		}
		setSelectedBarIds((prev) => {
			const next = new Set();
			visibleBarIds.forEach((id) => {
				if (prev.has(id)) {
					next.add(id);
				}
			});
			return next;
		});
	}, [assignMode, visibleBarIds]);
	const radiusHelperText = useMemo(() => {
		if (!selectedCity) {
			return "Select a city first.";
		}
		if (!radiusInputValid) {
			return "Enter a non-negative number.";
		}
		if (!selectedCityCenter) {
			return "Radius applies once bars have coordinates. Update missing locations if results look off.";
		}
		return "Set miles and press Apply to refine results.";
	}, [selectedCity, selectedCityCenter, radiusInputValid]);

	const handleCityChange = useCallback(
		(event, option) => {
			setSelectedCity(option);
			setRadiusMiles("");
			if (!option) {
				setSortMode("alphabetical");
			}
		},
		[setSortMode]
	);

	const handleSortChange = useCallback((event) => {
		setSortMode(event.target.value);
	}, []);

	const handleRegionChange = useCallback((event) => {
		setSelectedRegion(event.target.value || null);
	}, []);

	const handleAssignModeToggle = useCallback(() => {
		setAssignMode((current) => {
			const next = !current;
			if (next) {
				setSelectedRegion(null);
			}
			setAssignError(null);
			return next;
		});
	}, []);

	const handleAssignRegionChange = useCallback((event) => {
		setAssignRegionId(event.target.value || "");
	}, []);

	const handleToggleSelectBar = useCallback((id) => {
		setSelectedBarIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		setSelectedBarIds((prev) => {
			const allIds = visibleBarIds;
			if (prev.size === allIds.length && allIds.length > 0) {
				return new Set();
			}
			return new Set(allIds);
		});
	}, [visibleBarIds]);

	const handleClearFilters = useCallback(() => {
		setSelectedCity(null);
		setSelectedRegion(null);
		setRadiusMiles("");
		setSortMode("alphabetical");
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, selectedCity, selectedRegion, radiusMiles, assignMode, sortMode]);

	const handleRadiusApply = useCallback(() => {
		if (!radiusInputValid) {
			return;
		}
		if (radiusInput === "") {
			setRadiusMiles("");
			return;
		}
		const nextValue = Number.parseFloat(radiusInput);
		setRadiusMiles(Number.isFinite(nextValue) ? String(nextValue) : "");
	}, [radiusInput, radiusInputValid]);

	const handlePageChange = useCallback((_, value) => {
		setPendingLast(false);
		setPendingPage(null);

		const requiredCount = (value - 1) * pageSize + 1;
		const beyondLoaded = requiredCount > processedBars.length;
		const aimingForEnd = value >= paginationCount;

		if (aimingForEnd && !endOfResults) {
			setPendingLast(true);
			if (!loading) {
				getNext();
			}
			return;
		}

		if (beyondLoaded && !endOfResults) {
			setPendingPage(value);
			if (!loading) {
				getNext();
			}
			return;
		}

		setCurrentPage(Math.min(value, paginationCount));
	}, [endOfResults, getNext, loading, pageSize, paginationCount, processedBars.length]);

	const handlePageSizeChange = useCallback((event) => {
		const nextSize = Number(event.target.value) || DEFAULT_PAGE_SIZE;
		setPageSize(nextSize);
		setCurrentPage(1);
		setPendingPage(null);
		setPendingLast(false);
	}, []);

	const handleApplyRegionsClick = useCallback(() => {
		if (!assignRegionId) {
			setAssignError("Select a region before applying.");
			return;
		}
		if (selectedBarIds.size === 0) {
			setAssignError("Select at least one bar to assign.");
			return;
		}
		setAssignError(null);
		setConfirmAssignOpen(true);
	}, [assignRegionId, selectedBarIds]);

	const handleCancelAssign = useCallback(() => {
		setConfirmAssignOpen(false);
		setAssignError(null);
	}, []);

	const handleAssignConfirm = useCallback(async () => {
		setAssigning(true);
		setAssignError(null);

		try {
			await assignRegionsToBars(Array.from(selectedBarIds), assignRegionId);
			setSelectedBarIds(new Set());
			setAssignRegionId("");
			setConfirmAssignOpen(false);
		} catch (error) {
			setAssignError(error?.message || "Failed to assign regions");
		} finally {
			setAssigning(false);
		}
	}, [assignRegionId, assignRegionsToBars, selectedBarIds]);

	const navigateToAddBar = useCallback(() => {
		clearSelectedBar();
		history.push("/bar/new");
	}, [clearSelectedBar, history]);

	return (
		<Container>
			<div className="filters">
				<TextField
					className="filter-input"
					label="Search bars"
					placeholder="Search by name"
					variant="outlined"
					value={searchTerm}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search />
							</InputAdornment>
						)
					}}
				/>

				<Autocomplete
					className="filter-input"
					options={cityOptions}
					getOptionLabel={(option) => option?.label || ""}
					isOptionEqualToValue={(option, value) => option?.key === value?.key}
					value={selectedCity}
					onChange={handleCityChange}
					clearOnEscape
					renderInput={(params) => (
						<TextField
							{...params}
							label="City"
							placeholder="Filter by city"
							variant="outlined"
						/>
					)}
				/>

				<FormControl className="filter-input" variant="outlined">
					<InputLabel id="region-select-label">Region</InputLabel>
					<Select
						labelId="region-select-label"
						label="Region"
						value={selectedRegion || ""}
						onChange={handleRegionChange}
						disabled={assignMode}
					>
						<MenuItem value="">All Regions</MenuItem>
						{regions
							.filter((region) => region.Name && typeof region.Name === "string")
							.map((region) => (
								<MenuItem key={region.id} value={region.id}>
									{region.Name}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl className="filter-input" variant="outlined">
					<InputLabel id="sort-select-label">Sort By</InputLabel>
					<Select
						labelId="sort-select-label"
						label="Sort By"
						value={sortMode}
						onChange={handleSortChange}
					>
						<MenuItem value="alphabetical">Alphabetical</MenuItem>
						<MenuItem value="lastUpdated">Last Updated</MenuItem>
						<MenuItem value="distance" disabled={!canComputeDistances}>
							Distance
						</MenuItem>
					</Select>
				</FormControl>

					<TextField
						className="filter-input"
						label="Radius (mi)"
						type="number"
						variant="outlined"
						value={radiusInput}
						onChange={(event) => setRadiusInput(event.target.value)}
						disabled={!hasCitySelection}
						inputProps={{ min: 0, step: 1, inputMode: "decimal" }}
						autoComplete="off"
						helperText={radiusHelperText || undefined}
						error={!radiusInputValid}
					/>

					<Button
						variant="contained"
						color="primary"
						onClick={handleRadiusApply}
						disabled={!canApplyRadius}
						className="filters-button"
					>
						Apply Radius
					</Button>

				<Button
					variant="outlined"
					onClick={handleClearFilters}
					className="filters-button"
				>
					Clear Filters
				</Button>

				<Button
					color="primary"
					variant="contained"
					onClick={navigateToAddBar}
					className="filters-button create-bar"
				>
					Create Bar
				</Button>
			</div>

			<div className="assign-actions">
				<Button
					variant={assignMode ? "outlined" : "contained"}
					color={assignMode ? "inherit" : "primary"}
					onClick={handleAssignModeToggle}
				>
					{assignMode ? "Cancel" : "Assign Regions"}
				</Button>

				{assignMode && (
					<>
						<FormControl className="assign-region-select" variant="outlined" size="small">
							<InputLabel id="assign-region-select-label">Region</InputLabel>
							<Select
								labelId="assign-region-select-label"
								label="Region"
								value={assignRegionId}
								onChange={handleAssignRegionChange}
							>
								<MenuItem value="">Select Region</MenuItem>
								{regions
									.filter((region) => region.Name && typeof region.Name === "string")
									.map((region) => (
										<MenuItem key={region.id} value={region.id}>
											{region.Name}
										</MenuItem>
									))}
							</Select>
						</FormControl>

						<Button
							variant="contained"
							color="success"
							onClick={handleApplyRegionsClick}
							disabled={assigning || selectedBarIds.size === 0 || !assignRegionId}
						>
							{assigning ? "Applying..." : `Apply Regions (${selectedBarIds.size})`}
						</Button>
					</>
				)}

				{assignMode && assignError && (
					<span className="assign-error">{assignError}</span>
				)}
			</div>

			<div className="results-meta">
					<span>{pageSummaryText}</span>
					{loading ? <span className="loading-indicator">Loading...</span> : null}
			</div>

			{assignMode && (
				<div className="selection-header">
					<Checkbox
						checked={selectedBarIds.size === visibleBarIds.length && visibleBarIds.length > 0}
						indeterminate={selectedBarIds.size > 0 && selectedBarIds.size < visibleBarIds.length}
						onChange={handleToggleSelectAll}
					/>
					<span>
						Select bars without a region ({selectedBarIds.size} selected of {visibleBarIds.length})
					</span>
				</div>
			)}

			<div className="bars-container">
					{paginatedBars.map(({ bar, distance }) => (
						<BarListItem
							key={bar?.barId || bar?.id}
							barSnapshot={bar}
							distanceMiles={distance}
							assignMode={assignMode}
							selected={selectedBarIds.has(bar?.barId)}
							onToggleSelect={handleToggleSelectBar}
						/>
					))}
					{loading && paginatedBars.length === 0 && processedBars.length > 0 && (
						<NoResultsContainer>
							<span className="promotionName">Loading page...</span>
						</NoResultsContainer>
					)}
					{processedBars.length === 0 && !loading && (
						<NoResultsContainer>
							<span className="promotionName">No bars found</span>
						</NoResultsContainer>
					)}
			</div>

				<div className="pagination-footer">
					<div className="rows-per-page">
						<span className="label">Rows per page</span>
						<Select
							size="small"
							value={pageSize}
							onChange={handlePageSizeChange}
						>
							{PAGE_SIZE_OPTIONS.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</div>
					<div className="page-controls">
						<span className="page-summary">{pageSummaryText}</span>
						<Pagination
							count={paginationCount}
							page={paginationPage}
							onChange={handlePageChange}
							color="primary"
							shape="rounded"
							showFirstButton
							showLastButton
							siblingCount={1}
							boundaryCount={1}
							disabled={loading && processedBars.length === 0}
						/>
					</div>
				</div>

			<Dialog
				open={confirmAssignOpen}
				onClose={handleCancelAssign}
				aria-labelledby="assign-region-dialog-title"
				aria-describedby="assign-region-dialog-description"
			>
				<DialogTitle id="assign-region-dialog-title">Confirm Region Assignment</DialogTitle>
				<DialogContent>
					<DialogContentText id="assign-region-dialog-description">
						Assign {assignRegionName || "the selected"} region to {selectedBarIds.size} bar
						{selectedBarIds.size === 1 ? "" : "s"}? This will update their region metadata.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelAssign} disabled={assigning}>
						Cancel
					</Button>
					<Button onClick={handleAssignConfirm} color="primary" disabled={assigning}>
						{assigning ? "Assigning..." : "Accept"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;
	overflow: auto;
	padding: 24px;

	.filters {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		align-items: flex-start;
		margin-bottom: 16px;
	}

	.filter-input {
		min-width: 200px;
	}

	.assign-actions {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 12px;
		flex-wrap: wrap;
	}

	.assign-region-select {
		min-width: 220px;
	}

	.assign-error {
		color: ${THEME_MAP["color-danger-700"]};
		font-size: 0.9rem;
	}

	.filters-button {
		height: 56px;
	}

	.filters-button.create-bar {
		margin-left: auto;
	}

	.results-meta {
		margin-bottom: 8px;
		color: ${THEME_MAP["color-basic-600"]};
	}

	.bars-container {
		width: 100%;
		min-height: 100px;
		border-radius: 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-100"]};
		padding: 8px 0;
		margin-bottom: 16px;
		max-height: calc(100% - 180px);
		overflow-y: scroll;
	}

	.pagination-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px 0;
		border-top: 1px solid ${THEME_MAP["color-basic-300"]};
	}

	.rows-per-page {
		display: flex;
		align-items: center;
		gap: 8px;
		color: ${THEME_MAP["color-basic-700"]};
		font-weight: 600;
	}

	.page-controls {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		justify-content: flex-end;
		min-width: 260px;
		flex-wrap: wrap;
	}

	.page-summary {
		color: ${THEME_MAP["color-basic-700"]};
		white-space: nowrap;
	}

	.loading-indicator {
		color: ${THEME_MAP["color-primary-600"]};
		font-weight: 600;
		margin-left: 8px;
	}

	.selection-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}
`;

function BarListItem({ barSnapshot, distanceMiles, assignMode, selected, onToggleSelect }) {
	const [showDeleteAlert, setShowDeleteAlert] = useState(false);
	const bar = React.useMemo(
		() => normalizeBarSnapshot(barSnapshot),
		[barSnapshot]
	);
	const history = useHistory();
	const { setSelectedBar } = useBarUtils();
	const { removeBarFromSite } = useUserUtils();
	const regions = useRegions();

	if (!bar?.barId) {
		return null;
	}

	const handleClick = () => {
		if (assignMode) {
			if (onToggleSelect) {
				onToggleSelect(bar.barId);
			}
			return;
		}
		setSelectedBar(bar);
		history.push(`bar/${bar.barId}`);
	};

	const handleEditClick = (event) => {
		event.stopPropagation();
		event.preventDefault();
		handleClick();
	};

	const handleDeleteClick = (event) => {
		event.stopPropagation();
		event.preventDefault();
		setShowDeleteAlert(true);
	};

	const removeBarPermissions = async () => {
		try {
			await removeBarFromSite(bar.barId);
			window.location.href = "";
		} catch (err) {
			console.log(err);
		}
	};

	const dialogView = (
		<Dialog
			open={showDeleteAlert}
			onClose={() => setShowDeleteAlert(false)}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">
				{"Are you sure?"}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					Would you like to proceed with deleting this bar?
					<br />
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setShowDeleteAlert(false)}
					color="primary"
				>
					Cancel
				</Button>

				<Button
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						removeBarPermissions();
					}}
					color="primary"
					autoFocus
				>
					Delete
				</Button>
			</DialogActions>
		</Dialog>
	);

	const addressParts = parseAddressParts(bar.barAddress);
	const locationLabel = formatCityState(bar);
	const locationDetail = [
		addressParts.city,
		addressParts.state,
		addressParts.postalCode
	]
		.filter(Boolean)
		.join(" ");
	const locationSummary = locationDetail || locationLabel;
	const streetLine = addressParts.street;
	const regionName = regions.find(r => r.id === bar.regionId)?.Name;
	const lastUpdated = bar.updatedAt || bar.lastPromotionUpdateUTC || bar.createdAt;

	return (
		<>
			<BarItem onClick={handleClick}>
				{assignMode && (
					<div className="select-box" onClick={(event) => event.stopPropagation()}>
						<Checkbox
							checked={selected}
							onChange={() => {
								if (onToggleSelect) {
									onToggleSelect(bar.barId);
								}
							}}
						/>
					</div>
				)}
				<div className="details">
					<span className="barname">{bar.barName || "Bar Name"}</span>
					{locationSummary ? (
						<span className="location">{locationSummary}</span>
					) : null}
					{streetLine ? (
						<span className="barAddress">{streetLine}</span>
					) : null}
					{regionName ? (
						<span className="region">Region: {regionName}</span>
					) : null}
					<div className="meta">
						<span className="meta-item">Updated {formatDateTime(lastUpdated)}</span>
						{distanceMiles !== null && distanceMiles !== undefined ? (
							<span className="meta-item">
								{distanceMiles.toFixed(1)} mi away
							</span>
						) : null}
					</div>
				</div>
				<div className="actions">
					<Button
						className="edit-bar-button"
						variant="contained"
						color="primary"
						size="small"
						onClick={handleEditClick}
						disabled={assignMode}
					>
						Edit
					</Button>
					<Button
						className="delete-bar-button"
						variant="outlined"
						color="primary"
						size="small"
						onClick={handleDeleteClick}
						disabled={assignMode}
					>
						Delete
					</Button>
				</div>
			</BarItem>
			{dialogView}
		</>
	);
}

const BarItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 24px;
	width: 100%;
	margin: 4px 0;

	&:hover {
		background: ${THEME_MAP["color-primary-transparent-100"]};
		cursor: pointer;
	}

	.details {
		flex: 1;
		min-width: 0;

		span {
			display: block;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.barname {
			color: ${THEME_MAP["color-primary-700"]};
			font-size: 1.2rem;
		}

		.location {
			color: ${THEME_MAP["color-basic-600"]};
			font-weight: 500;
		}

		.barAddress {
			color: ${THEME_MAP["color-basic-600"]};
		}

		.region {
			color: ${THEME_MAP["color-basic-600"]};
			font-size: 0.95rem;
			margin-top: 2px;
		}

		.meta {
			display: flex;
			flex-wrap: wrap;
			gap: 12px;
			margin-top: 6px;
			color: ${THEME_MAP["color-basic-600"]};
			font-size: 0.9rem;
		}

		.meta-item {
			white-space: nowrap;
		}
	}

	.actions {
		display: flex;
		align-items: center;
		gap: 8px;
		z-index: 1;
	}

	.select-box {
		display: flex;
		align-items: center;
		justify-content: center;
		padding-left: 4px;
	}

	.edit-bar-button,
	.delete-bar-button {
		z-index: 20;
	}
`;

const NoResultsContainer = styled.div`
	padding: 8px 24px;
`;
